/**
 * QR Code Generator Module
 *
 * This module provides core QR code generation functionality using the qrcode library.
 * It generates QR matrices and calculates overlay sizes for center customization.
 *
 * Requirements: 2.6, 4.4, 10.1
 * - 2.6: Use error correction level H (high) to support center customization
 * - 4.4: Center overlay size calculated to fit within error correction capacity
 * - 10.1: Generate QR codes entirely client-side without backend API calls
 */

import QRCode from 'qrcode';
import type { QrTemplate } from '../types';

/**
 * QR code error correction levels
 * Level H provides 30% redundancy, allowing for center overlays
 */
export type ErrorCorrectionLevel = 'L' | 'M' | 'Q' | 'H';

/**
 * Represents a single module (pixel) in the QR code matrix
 * true = dark module, false = light module
 */
export type QrModuleValue = boolean;

/**
 * QR code matrix representation
 * Contains the module data and size information
 */
export interface QrMatrix {
  /** 2D array of module values (true = dark, false = light) */
  readonly modules: readonly (readonly QrModuleValue[])[];
  /** Size of the matrix (number of modules per side) */
  readonly size: number;
  /** QR code version (1-40) */
  readonly version: number;
}

/**
 * Configuration for QR code generation
 */
export interface QrGenerationConfig {
  /** The data to encode in the QR code */
  readonly data: string;

  /** Error correction level (default: H for center overlay support) */
  readonly errorCorrectionLevel: ErrorCorrectionLevel;

  /** Output size in pixels */
  readonly size: number;

  /** Template with customization options */
  readonly template: QrTemplate;

  /** Optional bay number for center label */
  readonly bayNumber?: number;
}

/**
 * Result of QR code generation
 */
export interface QrGenerationResult {
  readonly success: boolean;
  readonly dataUrl: string | null;
  readonly error: string | null;
}

/**
 * Maximum percentage of QR code that can be covered by center overlay
 * Error correction level H can recover up to 30% data loss
 * We use 28% as the maximum to leave headroom for the corner bay ID label
 */
export const MAX_OVERLAY_PERCENTAGE = 0.28;

/**
 * Generates a QR code matrix from the given data
 *
 * Uses the qrcode library's create() function to generate the raw QR matrix.
 * Always uses error correction level H for maximum redundancy to support
 * center overlays.
 *
 * @param data - The string data to encode in the QR code
 * @param errorLevel - Error correction level (default: 'H')
 * @returns QrMatrix containing the module data and metadata
 * @throws Error if QR code generation fails
 *
 * Requirements: 2.6, 10.1
 */
export function generateQrMatrix(
  data: string,
  errorLevel: ErrorCorrectionLevel = 'H'
): QrMatrix {
  if (!data || data.length === 0) {
    throw new Error('QR code data cannot be empty');
  }

  const qrCode = QRCode.create(data, {
    errorCorrectionLevel: errorLevel,
  });

  const { modules, version } = qrCode;
  const size = modules.size;

  // Convert the BitMatrix data to a 2D boolean array
  const moduleArray: QrModuleValue[][] = [];

  for (let row = 0; row < size; row++) {
    const rowData: QrModuleValue[] = [];
    for (let col = 0; col < size; col++) {
      // modules.get() returns 1 for dark modules, 0 for light
      rowData.push(modules.get(row, col) === 1);
    }
    moduleArray.push(rowData);
  }

  return {
    modules: moduleArray,
    size,
    version,
  };
}

/**
 * Calculates the maximum overlay size for center customization
 *
 * The overlay size is constrained to not exceed 30% of the QR code dimensions
 * to stay within the error correction capacity of level H.
 *
 * Property 5: Center Overlay Size Bounds
 * For any QR code size, the center overlay dimensions shall not exceed
 * 30% of the QR code dimensions.
 *
 * @param qrSize - The size of the QR code in pixels
 * @returns The maximum overlay size in pixels (always <= 30% of qrSize)
 *
 * Requirements: 4.4
 */
export function calculateOverlaySize(qrSize: number): number {
  if (qrSize <= 0) {
    return 0;
  }

  // Calculate 30% of the QR size
  const maxSize = Math.floor(qrSize * MAX_OVERLAY_PERCENTAGE);

  return maxSize;
}

/**
 * Validates that an overlay size is within acceptable bounds
 *
 * @param overlaySize - The proposed overlay size
 * @param qrSize - The QR code size
 * @returns true if the overlay size is within bounds
 */
export function isOverlaySizeValid(
  overlaySize: number,
  qrSize: number
): boolean {
  if (qrSize <= 0 || overlaySize < 0) {
    return false;
  }

  const maxAllowedSize = calculateOverlaySize(qrSize);
  return overlaySize <= maxAllowedSize;
}
